
import { graft } from '../utilities/Graft';
import { Signal } from '../utilities/async/Signal';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { asOnceOnly } from '../store/middleware/RunOnceMiddleware';
import { Key, KeyFacet } from '../models/KeyFacet';
import {
  IOperationPayload,
  IOperationHandler,
  getOperationTypeKey,
  processOperation,
  getOperationState,
  editQueue,
  IOperationHandlerPayload,
  cleanupOperationQueue,
  IQueuedOperation
} from '../store/actions/implementOperation';
import { IFacet } from '../models/Facet';
import { registerOperators, getOperationKeyParts } from '../store/reducers/operationMapReducer';
import { waitForNextStoreUpdate } from '../store/enhancers/NextStoreUpdateEnhancer';
import { ApiError, IApiErrorProps } from '@ms/utilities-error/lib/ApiError';
import { handleError } from '../operations/handleError';
import { mapObject } from '../utilities/objects';
import { updateItems } from '../store/reducers/itemMapReducer';
import {
  isOperationHandlerRegisteredFacet,
  operationQueueHandlerFacet,
  operationHandlerConfigurationFacet
} from '../facets/OperationFacets';
import { withTimeout } from '@ms/utilities-async/lib/decorators/withTimeout';
import { readConfiguration } from '../operations/configuration/readConfiguration';
import { CancelError } from '../utilities/async/CancelError';
import ErrorStrings from '../models/error/ErrorStrings.resx';
import { logQoS } from '../operations/logQoS';
import { getQosEndSchemaFromError } from '@ms/telemetry-manager/lib/utilities/getQosEndSchemaFromError';
import { ResultTypeEnum } from '@ms/telemetry-manager/lib/events/Qos.event';

const DEFAULT_MAX_PARALLEL_OPERATION_COUNT = 1;

/**
 * The default timeout duration allotted to the callbacks provided for operation handlers.
 * 1 minute is close to most browsers' default request timeouts, so this should not cause
 * much expected disruptions when working in web space. But it should help other runtime
 * environments prevent 'infinite' loading situations.
 */
const DEFAULT_OPERATION_TIMEOUT = 60000; // 1 minute

const DEFAULT_ENABLE_TIMEOUT = false;

const DEFAULT_ENABLE_QOS = false;

/**
 * @public
 */
export interface ICreateOperationHandlerBaseOptions {
  /**
   * The maximum number of items which may be processed in parallel.
   * Default is `1`.
   */
  maxParallelOperationCount?: number;
  /**
   * The default timeout allotted to the callback, in milliseconds.
   * Once the timeout elapses, the operation will be treated as canceled.
   */
  timeout?: number;
  /**
   * Whether or not to enable QoS reporting around the handler callback.
   * Overrides usage of `enableQos` from `operationHandlerConfigurationFacet`.
   */
  enableQos?: boolean;
}

/**
 * @public
 */
export type IAddon = IItemCacheThunkAction<void>;

/**
 * Creates a new 'addon' with a one-time initializer.
 *
 * An addon may be dispatched to an item cache store to initialize its behaviors.
 * An addon will only execute its initialization the first time it is dispatched to a store.
 * @public
 */
export function createAddon(initializer: () => IItemCacheThunkAction<void>): IItemCacheThunkAction<void> {
  return asOnceOnly(initializer());
}

/**
 * @public
 */
export interface ISingleOperationPayload<TKey extends object, TOptions> {
  itemKey: Key<TKey>;
  options: TOptions;
}

/**
 * The payload provided to the callback for batch operation handlers.
 * @public
 */
export interface IBatchOperationPayload<TKey extends object, TOptions> extends IOperationPayload<TOptions> {
  keyType: TKey;
  items: {
    [itemKey: string]: TOptions;
  };
}

/**
 * Creates an action which, when dispatched, registers an operation handler,
 * which is typically an event-driven worker which processes a specified operation queue.
 * @public
 */
export function registerOperationHandler<TKey extends object, TOperationType extends object>(
  keyFacet: KeyFacet<TKey>,
  operationTypeFacet: IFacet<TOperationType>
): (handler: IOperationHandler<TKey, TOperationType>) => IItemCacheThunkAction<void> {
  const operationTypeKey = getOperationTypeKey({
    keyFacet: keyFacet,
    operationTypeFacet: operationTypeFacet
  });

  return (handler: IOperationHandler<TKey, TOperationType>) => {
    return (dispatch: IItemCacheDispatch) => {
      const operator = dispatch(handler({
        key: operationTypeKey
      }));

      dispatch(registerOperators({
        operators: {
          [operationTypeKey]: operator
        }
      }));

      dispatch(updateItems({
        items: {
          [operationTypeKey]: {
            ...operationQueueHandlerFacet.pack(operator),
            ...isOperationHandlerRegisteredFacet.pack(true)
          }
        }
      }));

      dispatch(processOperation({
        key: operationTypeKey
      }));
    };
  };
}

/**
 * @public
 */
export interface IOperationHandlerErrorProps extends IApiErrorProps {
  // Nothing.
}

/**
 * An error to be thrown by a single item operation handler which indicates
 * that the handler callback failed, and has recongnized the error.
 * @public
 */
export class OperationHandlerError extends ApiError {
  constructor(props: IOperationHandlerErrorProps = {}) {
    super(props);
  }
}

/**
 * @public
 */
export interface IBatchedItemsOperationHandlerErrorProps extends IApiErrorProps {
  items: {
    [itemKey: string]: Error;
  };
}

/**
 * An error to be thrown by a batched item operation handler which indicates
 * that some items in the batched have failed, and the rest have succeeded.
 * The individual errors per-item should be instances of `OperationHandlerError`
 * if possible, which indicates that the error was recognized.
 * @public
 */
export class BatchedItemsOperationHandlerError extends ApiError {
  public readonly items: {
    [itemKey: string]: Error;
  };

  constructor(props: IBatchedItemsOperationHandlerErrorProps) {
    super(props);
    this.items = props.items;
  }
}

/**
 * @public
 */
export type IOperationHandlerThunkAction = IItemCacheThunkAction<Promise<void>>;

/**
 * Represents the options for configuration a single operation handler.
 * @public
 */
export interface ICreateSingleItemOperationHandlerOptions extends ICreateOperationHandlerBaseOptions {
  /**
   * Whether or not to merge all pending operations for the same key.
   * The default is `true`.
   */
  mergeForSameItemKey?: boolean;
  /**
   * Whether or not to process the most recent queued operations first.
   *
   * Default is 'false'
   */
  prioritizeRecent?: boolean;
  /**
   * How many operations should remain in the queue after an allocation pass has been made.
   * If greater or equal to 0, will cancel operations (per the prioritizeRecent value)
   * until the queue meets this value.
   *
   * Default is 'undefined'.
   */
  maxQueueSize?: number;
}

/**
 * Represents a factory for single item operation handlers.
 * When invoked, creates a handler which will execute the given callback for each item
 * in the target operation queue.
 * @public
 */
export interface ISingleItemOperationHandlerFactory {
  <TKey extends object, TOptions extends object>(
    singleOperator: (payload: ISingleOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions>;
}

/**
 * Implements a data source operator by processing operations in the queue
 * one item at a time.
 * Data sources should use this function if it is not practical to process multiple
 * operations simultaneously.
 * @public
 */
export function createSingleItemOperationHandler(options: ICreateSingleItemOperationHandlerOptions = {}):
  ISingleItemOperationHandlerFactory {
  const {
    mergeForSameItemKey = true,
    prioritizeRecent = false,
    maxQueueSize = undefined,
    ...coreOptions
  } = options;

  return <TKey extends object, TOptions extends object>(
    singleOperator: (payload: ISingleOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions> => {
    return (payload: IOperationHandlerPayload<TKey, TOptions>): IItemCacheThunkAction<() => void> => {
      const {
        key
      } = payload;

      const {
        keyFacet,
        operationTypeFacet
      } = getOperationKeyParts(key);

      return (dispatch: IItemCacheDispatch) => {
        let workerCount = 0;

        let pendingAllocation = false;

        interface ITask {
          operation: IQueuedOperation<TKey, TOptions>;
          startSignals: Signal<void>[];
          completeSignals: Signal<void>[];
        }

        const tasksByItemKey: {
          [itemKey: string]: boolean;
        } = {};

        async function allocate(): Promise<void> {
          try {
            const {
              maxParallelOperationCount
            } = dispatch(getFinalHandlerOptions(coreOptions, key));

            const pendingTasks: ITask[] = [];

            while (workerCount < maxParallelOperationCount) {
              const operationState = dispatch(getOperationState({
                key: key
              }));

              if (!operationState) {
                break;
              }

              const {
                queue
              } = operationState;

              if (!queue || !queue.length) {
                break;
              }

              let operation: IQueuedOperation<TKey, TOptions> | undefined;

              const newQueue: IQueuedOperation<TKey, TOptions>[] = [];
              const startSignals: Signal<void>[] = [];
              const completeSignals: Signal<void>[] = [];

              const addToOperation = (nextOperation: IQueuedOperation<TKey, TOptions>): void => {
                operation = operation ? graft(operation, {
                  options: nextOperation.options
                }) : nextOperation;

                startSignals.push(nextOperation.startSignal);
                completeSignals.push(nextOperation.completeSignal);
              };

              // Depending on configuration, either iterate the queue backwards or forwards.
              for (
                let i = prioritizeRecent ? queue.length - 1 : 0;
                prioritizeRecent ? i >= 0 : i < queue.length;
                prioritizeRecent ? i-- : i++
              ) {
                const nextOperation = queue[i];

                if (!operation) {
                  // If there has not been an initial operation chosen, select the first one
                  // that is eligible to execute.
                  if (!mergeForSameItemKey || !tasksByItemKey[nextOperation.itemKey]) {
                    addToOperation(nextOperation);
                  }
                } else if (mergeForSameItemKey && nextOperation.itemKey === operation.itemKey) {
                  addToOperation(nextOperation);
                } else {
                  // Put any non-allocated tasks back in the edited queue.
                  if (prioritizeRecent) {
                    newQueue.unshift(nextOperation);
                  } else {
                    newQueue.push(nextOperation);
                  }
                }
              }

              if (!operation) {
                // No operation was allocated, so stop allocation for this cycle.
                // The loop will be entered again once an existing task completes
                // or the queue is modified.
                break;
              }

              tasksByItemKey[operation.itemKey] = true;

              workerCount++;

              pendingTasks.push({
                operation,
                startSignals,
                completeSignals
              });

              const operationsToCancel: IQueuedOperation<TKey, TOptions>[] = [];

              if (workerCount === maxParallelOperationCount && typeof maxQueueSize === 'number') {
                while (newQueue.length > maxQueueSize) {
                  const operationToCancel = prioritizeRecent ? newQueue.shift() : newQueue.pop();

                  if (operationToCancel) {
                    operationsToCancel.push(operationToCancel);
                  }
                }
              }

              dispatch(editQueue({
                key: key,
                queue: newQueue
              }));

              if (operationsToCancel.length > 0) {
                const error = new CancelError();

                for (const cancelledOperation of operationsToCancel) {
                  cancelledOperation.startSignal.reject(error);
                  cancelledOperation.completeSignal.reject(error);
                }
              }
            }

            pendingAllocation = false;

            for (const task of pendingTasks) {
              work(task);
            }
          } catch (e) {
            // If anything terminated the worker, it is important to bubble that to the host app for troubleshooting.
            dispatch(handleError({
              error: e
            }));
          }
        }

        async function work({
          operation,
          startSignals,
          completeSignals
        }: ITask): Promise<void> {
          try {
            const {
              enableQos,
              timeout
            } = dispatch(getFinalHandlerOptions(coreOptions, key));

            for (const startSignal of startSignals) {
              // Indicate that work is starting since the operation will be dispatched to the handler.
              startSignal.resolve();
            }

            const {
              resolve,
              reject,
              promise
            } = new Signal<void>();

            const endQos = enableQos ? dispatch(logQoS({
              name: `singleOperationHandler.${keyFacet.debugName}.${operationTypeFacet.debugName}`
            })) : undefined;

            try {
              await withTimeout(async () => dispatch(singleOperator({
                itemKey: operation.itemKey,
                options: <TOptions>operation.options
              })), timeout)();

              if (endQos) {
                dispatch(endQos({
                  resultType: ResultTypeEnum.Success
                }));
              }

              resolve();
            } catch (e) {
              if (endQos) {
                dispatch(endQos(getQosEndSchemaFromError(e)));
              }

              reject(e instanceof Error ? e : new OperationHandlerError({
                message: `The handler for '${key}' threw a non-error: ${e}`
              }));

              // Do not rethrow the error or it will terminate queue processing.
              // It is up to each operation implementation to decide how to handle the rejected promise.
            }

            const {
              completeSignals: remainingCompleteSignals
            } = dispatch(cleanupOperationQueue({
              key: key,
              items: {
                [operation.itemKey]: [operation.options]
              }
            }));

            for (const signal of [...completeSignals, ...remainingCompleteSignals]) {
              promise.then(signal.resolve, signal.reject);
            }

            if (mergeForSameItemKey) {
              delete tasksByItemKey[operation.itemKey];
            }

            await dispatch(waitForNextStoreUpdate());

            workerCount--;

            // Once a worker completes, it should refill the queue if possible.

            if (!pendingAllocation) {
              pendingAllocation = true;
              allocate();
            }
          } catch (e) {
            // If anything terminated the worker, it is important to bubble that to the host app for troubleshooting.
            dispatch(handleError({
              error: e
            }));
          }
        }

        return () => {
          // Refill the queue if possible.

          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        };
      };
    };
  };
}

/**
 * Represents the options for configuring a batched operation handler.
 * @public
 */
export interface ICreateBatchedItemOperationHandlerOptions<TKey extends object = {}, TOptions extends object = {}>
  extends ICreateOperationHandlerBaseOptions {
  /**
   * The maximum number of distinct items to pull into a batch.
   * Default is all items.
   */
  maxItemCountPerBatch?: number;
  /**
   * Optional discriminator to determine if two operations should be treated as part of the same batch.
   * Default always returns `true` for any pair of operations.
   */
  areSameBatch?: (
    payload1: ISingleOperationPayload<TKey, TOptions>,
    payload2: ISingleOperationPayload<TKey, TOptions>
  ) => boolean;
}

/**
 * Represents a factory for batched item operation handlers.
 * When invoked, creates a handler which invokes the specified callback
 * across multiple items within the target operation queue.
 * @public
 */
export interface IBatchedItemsOperationHandlerFactory {
  <TKey extends object, TOptions extends object>(
    batchedOperator: (payload: IBatchOperationPayload<TKey, TOptions>
    ) => IOperationHandlerThunkAction): IOperationHandler<TKey, TOptions>;
}

/**
 * Implements a data source operator by processing all pending operations as a batch.
 * Data sources should use this function to minimize I/O operations when a lot
 * of items need to be processed.
 * @public
 */
export function createBatchedItemsOperationHandler(options: ICreateBatchedItemOperationHandlerOptions = {}):
  IBatchedItemsOperationHandlerFactory {
  const {
    maxItemCountPerBatch = Infinity,
    areSameBatch = defaultAreSameBatch,
    ...coreOptions
  } = options;

  return <TKey extends object, TOptions extends object>(
    batchedOperator: (payload: IBatchOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions> => {
    return (payload: IOperationHandlerPayload<TKey, TOptions>): IItemCacheThunkAction<() => void> => {
      const {
        key
      } = payload;

      const {
        keyFacet,
        operationTypeFacet
      } = getOperationKeyParts(key);

      return (dispatch: IItemCacheDispatch) => {
        let workerCount = 0;

        let pendingAllocation = false;

        interface ITask {
          batchOperation: IBatchOperationPayload<TKey, TOptions>;
          startSignals: Signal<void>[];
          completeSignals: Signal<void>[];
          completeSignalsMap: {
            [itemKey: string]: Signal<void>[];
          };
        }

        // Keep track of which items have already been allocated to active tasks.
        // Once a batch containing an item is allocated, the item may not be allocated
        // to a new batch until the current one completes.
        const tasksByItemKey: {
          [itemKey: string]: boolean;
        } = {};

        async function allocate(): Promise<void> {
          try {
            const {
              maxParallelOperationCount
            } = dispatch(getFinalHandlerOptions(coreOptions, key));

            const pendingTasks: ITask[] = [];

            while (workerCount < maxParallelOperationCount) {
              const operationState = dispatch(getOperationState({
                key: key
              }));

              if (!operationState) {
                break;
              }

              const {
                queue
              } = operationState;

              if (!queue || !queue.length) {
                break;
              }

              let operation: IBatchOperationPayload<TKey, TOptions> = {
                items: {}
              } as IBatchOperationPayload<TKey, TOptions>;

              const startSignals: Signal<void>[] = [];
              const completeSignals: Signal<void>[] = [];
              const completeSignalsMap: ITask['completeSignalsMap'] = {};

              const newQueue: IQueuedOperation<TKey, TOptions>[] = [];

              const addOperationToBatch = (nextOperation: IQueuedOperation<TKey, TOptions>): void => {
                operation = graft(operation, {
                  items: {
                    [nextOperation.itemKey]: nextOperation.options
                  }
                });

                startSignals.push(nextOperation.startSignal);
                completeSignals.push(nextOperation.completeSignal);

                const completeSignalsForItem = completeSignalsMap[nextOperation.itemKey] || (
                  completeSignalsMap[nextOperation.itemKey] = []
                );
                completeSignalsForItem.push(nextOperation.completeSignal);
              };

              let firstOperation: IQueuedOperation<TKey, TOptions> | undefined;

              for (const nextOperation of queue) {
                if (!firstOperation) {
                  if (!tasksByItemKey[nextOperation.itemKey]) {
                    firstOperation = nextOperation;

                    addOperationToBatch(nextOperation);
                  }
                } else if (
                  (operation.items[nextOperation.itemKey] || Object.keys(operation.items).length < maxItemCountPerBatch) &&
                  areSameBatch({
                    itemKey: firstOperation.itemKey,
                    options: firstOperation.options
                  }, {
                      itemKey: nextOperation.itemKey,
                      options: nextOperation.options
                    })
                ) {
                  addOperationToBatch(nextOperation);
                } else {
                  // Once the maximum number of items has been reached,
                  // or the operation is not deemed part of the same batch.
                  // skip this operation, leaving it in the queue.
                  newQueue.push(nextOperation);
                }
              }

              if (!firstOperation) {
                // No operation was allocated, so stop allocation for this round.
                break;
              }

              for (const itemKey of Object.keys(operation.items)) {
                tasksByItemKey[itemKey] = true;
              }

              workerCount++;

              pendingTasks.push({
                batchOperation: operation,
                startSignals,
                completeSignals,
                completeSignalsMap
              });

              dispatch(editQueue({
                key: key,
                queue: newQueue
              }));
            }

            pendingAllocation = false;

            for (const task of pendingTasks) {
              work(task);
            }
          } catch (e) {
            // If anything terminated the worker, it is important to bubble that to the host app for troubleshooting.
            dispatch(handleError({
              error: e
            }));
          }
        }

        async function work({
          batchOperation,
          startSignals,
          completeSignals,
          completeSignalsMap
        }: ITask): Promise<void> {
          try {
            const {
              enableQos,
              timeout
            } = dispatch(getFinalHandlerOptions(coreOptions, key));

            for (const startSignal of startSignals) {
              startSignal.resolve();
            }

            const {
              resolve,
              reject,
              promise
            } = new Signal<void>();

            const endQos = enableQos ? dispatch(logQoS({
              name: `batchedOperationHandler.${operationTypeFacet.debugName}.${keyFacet.debugName}`
            })) : undefined;

            try {
              await withTimeout(async () => dispatch(batchedOperator(batchOperation)), timeout)();

              if (endQos) {
                dispatch(endQos({
                  resultType: ResultTypeEnum.Success
                }));
              }

              resolve();
            } catch (e) {
              if (endQos) {
                dispatch(endQos(getQosEndSchemaFromError(e)));
              }

              if (e instanceof BatchedItemsOperationHandlerError) {
                // If the handler threw a batched error, it means that some of the tasks may have actually succeeded.

                for (const itemKey of Object.keys(completeSignalsMap)) {
                  // For every item in the batch, check if it was assigned an error.
                  // If it was, reject its associated tasks. If it was not, assume it was completed and resolve its associated tasks.
                  const completeSignalsForItem = completeSignalsMap[itemKey];
                  const itemError = e.items[itemKey];

                  if (itemError) {
                    const error = itemError instanceof Error ? itemError : new OperationHandlerError({
                      message: `The handler for '${key}' and item '${itemKey}' threw a non-error: ${itemError}`
                    });

                    for (const completeSignal of completeSignalsForItem) {
                      completeSignal.reject(error);
                    }
                  } else {
                    for (const completeSignal of completeSignalsForItem) {
                      completeSignal.resolve();
                    }
                  }
                }
              }

              reject(e instanceof Error ? e : new OperationHandlerError({
                message: `The handler for '${key}' threw a non-error: ${e}`
              }));

              // Do not rethrow the error or it will terminate queue processing.
              // It is up to each operation implementation to decide how to handle the rejected promise.
            }

            const {
              completeSignals: remainingCompleteSignals
            } = dispatch(cleanupOperationQueue({
              key: key,
              items: mapObject(batchOperation.items, (itemOptions: TOptions) => ([itemOptions]))
            }));

            for (const signal of [...completeSignals, ...remainingCompleteSignals]) {
              promise.then(signal.resolve, signal.reject);
            }

            for (const itemKey of Object.keys(batchOperation.items)) {
              delete tasksByItemKey[itemKey];
            }

            await dispatch(waitForNextStoreUpdate());

            workerCount--;

            if (!pendingAllocation) {
              pendingAllocation = true;
              allocate();
            }
          } catch (e) {
            // If anything terminated the worker, it is important to bubble that to the host app for troubleshooting.
            dispatch(handleError({
              error: e
            }));
          }
        }

        return () => {
          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        };
      };
    };
  };
}

/**
 * Data source operator which loads its implementation asynchronously on
 * the first execution.
 * @public
 */
export function createDeferredOperationHandler<TKey extends object, TOptions extends object>(
  loadOperator: () => Promise<IOperationHandler<TKey, TOptions>>
): IOperationHandler<TKey, TOptions> {
  return (payload: IOperationHandlerPayload<TKey, TOptions>) => {
    const {
      key
    } = payload;

    const {
      keyFacet,
      operationTypeFacet
    } = getOperationKeyParts(key);

    return (dispatch: IItemCacheDispatch) => {
      let loading = false;

      const load = async () => {
        try {
          const dataSourceOperator = await loadOperator();

          if (!dataSourceOperator) {
            throw new Error(ErrorStrings.DefaultTitle);
          }

          // Register the real operation handler, replacing the placeholder loader.
          dispatch(registerOperationHandler(keyFacet, operationTypeFacet)(dataSourceOperator));
        } catch (e) {
          const error = e instanceof Error ? e : new OperationHandlerError({
            message: ErrorStrings.DefaultTitle
          });

          const operationState = dispatch(getOperationState({
            key: key
          }));

          if (operationState && operationState.queue && operationState.queue.length > 0) {
            // If there are pending operations, and the handler has failed to load,
            // these operations should fail, which will indicate to the original caller that
            // it should consider retrying.

            const startSignals: Signal<void>[] = [];
            const completeSignals: Signal<void>[] = [];

            for (const operation of operationState.queue) {
              startSignals.push(operation.startSignal);
              completeSignals.push(operation.completeSignal);
            }

            dispatch(editQueue({
              key,
              queue: []
            }));

            for (const startSignal of startSignals) {
              startSignal.reject(error);
            }

            for (const completeSignal of completeSignals) {
              completeSignal.reject(error);
            }
          }

          // If there was an error loading an operator, it is a serious bug worth troubleshooting.
          dispatch(handleError({
            error
          }));
        } finally {
          // Reset loading state. If the operator failed to load,
          // triggering this operator will attempt to load it again.
          loading = false;
        }
      };

      return () => {
        // Placeholder operation handler which invokes the loader callback
        // as long as there are pending operations to process.
        const operationState = dispatch(getOperationState({
          key: key
        }));

        if (operationState && operationState.queue && operationState.queue.length > 0) {
          if (!loading) {
            loading = true;

            load();
          }
        }
      };
    };
  };
}

/**
 * Creates an action which, when dispatched, gets the final
 * configuration options for an operation handler.
 * Dispatch this action each time a handler performs a discrete task,
 * in order for the configuration to take effect 'live'.
 */
function getFinalHandlerOptions(
  options: ICreateOperationHandlerBaseOptions,
  operationTypeKey: string
): IItemCacheThunkAction<{
  timeout: number;
  enableQos: boolean;
  maxParallelOperationCount: number;
}> {
  return (dispatch: IItemCacheDispatch) => {
    const {
      operationHandler = {}
    } = dispatch(readConfiguration({
      operationHandler: operationHandlerConfigurationFacet
    }));

    const {
      defaultTimeout = DEFAULT_OPERATION_TIMEOUT,
      enableTimeout: defaultEnableTimeout = DEFAULT_ENABLE_TIMEOUT,
      enableQos: defaultEnableQos = DEFAULT_ENABLE_QOS,
      handlers: {
        [operationTypeKey]: handlerConfig = {}
      } = {}
    } = operationHandler;

    const {
      timeout: optionTimeout = defaultTimeout,
      maxParallelOperationCount: optionMaxParallelOperationCount = DEFAULT_MAX_PARALLEL_OPERATION_COUNT,
      enableQos: optionEnableQos = defaultEnableQos,
    } = options;

    const {
      timeout: handlerTimeout = optionTimeout,
      enableQos: handlerEnableQos = optionEnableQos,
      maxParallelOperationCount: handlerMaxParallelOperationCount = optionMaxParallelOperationCount
    } = handlerConfig;

    return {
      timeout: defaultEnableTimeout ? handlerTimeout : Infinity,
      enableQos: handlerEnableQos,
      maxParallelOperationCount: handlerMaxParallelOperationCount
    };
  };
}

function defaultAreSameBatch(): boolean {
  return true;
}
